import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, filter, map, startWith, switchMap } from 'rxjs';

import { CmsPageService, LoaderType } from '@fcom/common';
import { CmsCollection, CmsContentType, CmsImageData, CmsTemplate, CmsViewType } from '@fcom/core-api';
import { RootPaths } from '@fcom/core/constants';
import { CmsDataService } from '@fcom/core';
import { finShare } from '@fcom/rx';
import { getBackgroundImageForCmsTemplate } from '@fcom/cms/services/cms-utils/cms-utils';
import { filterWithoutType, findByType } from '@fcom/cms';
import { TourOperator } from '@fcom/common-booking/interfaces/tour-operator.interface';

import { ManageBookingFeatureState } from '../../../interfaces';
import { getLoading } from '../../../store';
import { MmbError } from '../../../enums';

@Component({
  selector: 'fin-mmb-entry-route',
  templateUrl: './manage-booking-entry-route.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingEntryRouteComponent implements OnInit {
  readonly LoaderType = LoaderType;
  public readonly MmbError = MmbError;
  loading$: Observable<boolean>;
  template$: Observable<{ loading: boolean; data: CmsTemplate }>;
  backgroundImage$: Observable<CmsImageData>;
  tourOperatorImage$: Observable<CmsImageData>;
  operator$: Observable<TourOperator | undefined>;
  disruptions$: Observable<CmsCollection>;
  mainContent$: Observable<CmsTemplate>;
  changeFlightModalOpen$: Observable<boolean>;

  constructor(
    private store$: Store<ManageBookingFeatureState>,
    private pageService: CmsPageService,
    private cmsDataService: CmsDataService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.operator$ = this.route.data.pipe(map((params) => params?.operator));
    this.loading$ = this.store$.pipe(getLoading());
    this.template$ = this.operator$.pipe(
      switchMap((operator) => {
        const operatorPath = operator ? `/${operator}` : '';
        return this.pageService
          .getPageDataAndSetMeta(RootPaths.MANAGE_BOOKING_ROOT + operatorPath)
          .pipe(map((data) => ({ loading: false, data })));
      }),
      startWith({ loading: true, data: null }),
      finShare()
    );

    const contents$ = this.template$.pipe(
      filter((template) => Boolean(template.data)),
      map((template) => template.data)
    );
    this.mainContent$ = contents$.pipe(filterWithoutType(CmsViewType.DISRUPTIONS, 'main'));
    this.disruptions$ = contents$.pipe(
      map((c) => c.main),
      findByType(CmsViewType.DISRUPTIONS, false)
    );

    this.backgroundImage$ = getBackgroundImageForCmsTemplate(this.template$.pipe(map((t) => t.data)));

    this.tourOperatorImage$ = this.template$.pipe(
      map((template) => template.data.main),
      map(
        (content) =>
          content?.find((obj) => obj.contentType === CmsContentType.CMPlaceholder)?.localSettings?.tourOperatorLogo
            ?.jsonUrl
      ),
      filter(Boolean),
      switchMap((logoUrl: string) => this.cmsDataService.getFragmentJson<CmsImageData>(logoUrl))
    );
  }
}
